// Live PaperTrail feed for user 1. Last 100 versions, filtered by model in the browser.
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import Select from "react-select";
import { Alert, Badge, Spinner } from "reactstrap";
import SlidingPane from "modules/5_view_helpers/web/SlidingPane";
import { createRecentActivitiesSubscription } from "channels/recent_activities_channel";

const LIMIT = 100;

const EVENT_COLORS = {
  create: "success",
  update: "primary",
  destroy: "danger",
};

const mergeActivities = (current, incoming) =>
  _.take(
    _.orderBy(_.uniqBy([...incoming, ...current], "id"), ["created_at", "id"], ["desc", "desc"]),
    LIMIT,
  );

function mapStateToProps(state) {
  return {
    current_user: state.current_user,
  };
}

const RecentActivitySidebar = ({ open, setOpen, current_user }) => {
  const [activities, setActivities] = useState([]);
  const [modelFilter, setModelFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || current_user?.id !== 1) {
      return undefined;
    }

    let ignore = false;
    setLoading(true);
    setError(null);

    const subscription = createRecentActivitiesSubscription((activity) => {
      setActivities((current) => mergeActivities(current, [activity]));
    });

    axios
      .get("/recent_activities.json")
      .then(({ data }) => {
        if (!ignore) {
          setActivities((current) => mergeActivities(current, data || []));
        }
      })
      .catch((requestError) => {
        if (!ignore) {
          setError(_.get(requestError, "response.data.0") || "Could not load activity.");
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
      subscription.unsubscribe();
    };
  }, [open, current_user?.id]);

  const modelOptions = useMemo(
    () =>
      _.sortBy(_.uniqBy(activities, "item_type"), "model_label").map((activity) => ({
        value: activity.item_type,
        label: activity.model_label || activity.item_type,
      })),
    [activities],
  );

  const visible = modelFilter ? _.filter(activities, { item_type: modelFilter.value }) : activities;

  if (current_user?.id !== 1) {
    return null;
  }

  let body;
  if (loading && _.isEmpty(activities)) {
    body = (
      <div className="text-center mt-3">
        <Spinner type="border" color="primary" />
      </div>
    );
  } else if (error) {
    body = <Alert color="danger">{error}</Alert>;
  } else if (_.isEmpty(visible)) {
    body = <div className="text-muted">No activity found.</div>;
  } else {
    body = _.map(visible, (activity) => (
      <div key={activity.id} className="border-bottom py-2">
        <div className="d-flex align-items-center justify-content-between">
          <Badge color={EVENT_COLORS[activity.event] || "secondary"} pill>
            {_.startCase(activity.event)}
          </Badge>
          <small className="text-muted">{moment(activity.created_at).format("DD/MM/YYYY HH:mm")}</small>
        </div>
        <div className="mt-1">{activity.model_label || activity.item_type}</div>
        <small className="text-muted">
          {activity.author_name} · #{activity.item_id}
        </small>
        {!_.isEmpty(activity.changed_fields) && (
          <div className="text-muted small">{activity.changed_fields.map(_.startCase).join(", ")}</div>
        )}
      </div>
    ));
  }

  return (
    <SlidingPane isOpen={open} onRequestClose={() => setOpen(false)} title="Recent activity" width="420px">
      <Select
        className="mb-3"
        classNamePrefix="react-select"
        instanceId="recent-activity-model"
        isClearable
        options={modelOptions}
        placeholder="All models"
        value={modelFilter}
        onChange={setModelFilter}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
      {body}
    </SlidingPane>
  );
};

export default connect(mapStateToProps)(RecentActivitySidebar);
